/**
 * APIKey est la projection UI d'une *apikey.APIKey backend (sans hash,
 * sans la valeur en clair sauf au moment de la création).
 */
import type { Tier } from '../tiers'

/**
 * RoutingEntry : un maillon de cascade {providerKeyId, model}. RoutingMap :
 * pour chaque tier, une cascade ordonnée (fallback). Routing v2 — remplace
 * le plan_id.
 */
export interface RoutingEntry {
    readonly providerKeyId: string
    readonly model: string
    /** Poids pour la stratégie de load balancing "weighted" (2-B). Défaut 1. */
    readonly weight?: number
}
export type RoutingMap = Partial<Record<Tier, readonly RoutingEntry[]>>

/** Stratégie de load balancing applicable à un tier (chantier 2-B). */
export const LB_STRATEGIES = ['weighted', 'latency'] as const
export type LBStrategy = (typeof LB_STRATEGIES)[number]
/** Stratégie LB par tier. Absent ⇒ défaut "weighted". */
export type RoutingStrategyMap = Partial<Record<Tier, string>>

/**
 * APIKeyMode distingue les ApiKey classiques (Sprint 9 V2 — Routing + cascade
 * ProviderKey, le tenant possède les clés provider) des ApiKey passthrough
 * (Sprint 10 — subscription pass-through, le user envoie son propre Bearer
 * OAuth de Claude Code / Codex CLI / Copilot via PreLLM qui sanitize +
 * audit).
 */
export type APIKeyMode = 'api_key' | 'passthrough'

/**
 * PassthroughProvider : valeur posée sur l'ApiKey quand mode='passthrough'.
 * Indique l'adapter cible côté data-plane (route /u/{pkLive}/...).
 */
export type PassthroughProvider = 'anthropic_oauth' | 'openai_codex' | 'github_copilot'

export const PASSTHROUGH_PROVIDERS: readonly PassthroughProvider[] = [
    'anthropic_oauth',
    'openai_codex',
    'github_copilot',
] as const

export interface APIKey {
    readonly id: string
    readonly tenantId: string
    readonly createdByUserId: string
    readonly userId?: string
    /** Équipe d'imputation des coûts (FinOps). Vide ⇒ non imputée. */
    readonly teamId?: string
    /** Tag projet/application (FinOps) : propagé dans l'audit, filtre « Application ». */
    readonly projectTag?: string
    /** Profil de Route effectif (route_id). Vide ⇒ routing inline / défaut. */
    readonly routeId?: string
    readonly routing: RoutingMap
    readonly routingStrategies?: RoutingStrategyMap
    readonly mode: APIKeyMode
    readonly passthroughProvider?: PassthroughProvider
    readonly name: string
    readonly prefix: string
    readonly environment: 'live' | 'test'
    readonly scopes: APIKeyScopes
    readonly revoked: boolean
    readonly createdAt: string
    readonly lastUsedAt?: string
}

export function isPassthrough(apiKey: APIKey): boolean {
    return apiKey.mode === 'passthrough'
}

export interface APIKeyScopes {
    readonly models?: readonly string[]
    readonly routes?: readonly string[]
    readonly budgetDailyEUR?: number
    readonly budgetMonthlyEUR?: number
    readonly rateLimitRPM?: number
    readonly rateLimitTPM?: number
    readonly piiModesAllowed?: readonly string[]
    readonly ipAllowlist?: readonly string[]
    readonly expiresAt?: string
}

/**
 * APIKeyCreated : retour de POST /apikeys. Le `secret` est la valeur en
 * clair, exposée UNE SEULE FOIS. Le frontend l'affiche avec un warning.
 */
export interface APIKeyCreated {
    readonly apikey: APIKey
    readonly secret: string
}

export interface CreateAPIKeyInput {
    readonly name: string
    readonly environment?: 'live' | 'test'
    readonly tenantId?: string
    readonly userId?: string
    readonly routing?: RoutingMap
    readonly routingStrategies?: RoutingStrategyMap
    readonly mode?: APIKeyMode
    readonly passthroughProvider?: PassthroughProvider
}

export type APIKeyErrorCode =
    | 'invalid_body'
    | 'invalid_expires_at'
    | 'apikey_not_found'
    | 'tenant_scope'
    | 'csrf_required'
    | 'csrf_invalid'
    | 'unauthorized'
    | 'forbidden'
    | 'internal_error'
    | 'network_error'
    | 'invalid_passthrough_provider'
    | 'passthrough_with_routing'
    | 'passthrough_missing_provider'
    | 'passthrough_provider_on_api_key'
    | 'team_tenant_mismatch'
    | 'team_not_owned'
    | 'route_tenant_mismatch'
    | 'route_not_found'
    | 'unknown'

export class APIKeyError extends Error {
    readonly code: APIKeyErrorCode
    readonly httpStatus: number | null
    constructor(code: APIKeyErrorCode, message: string, httpStatus: number | null = null) {
        super(message)
        this.code = code
        this.httpStatus = httpStatus
        this.name = 'APIKeyError'
    }
}

export function isAPIKeyError(err: unknown): err is APIKeyError {
    return err instanceof APIKeyError
}

export const KNOWN_APIKEY_CODES: readonly APIKeyErrorCode[] = [
    'invalid_body',
    'invalid_expires_at',
    'apikey_not_found',
    'tenant_scope',
    'csrf_required',
    'csrf_invalid',
    'unauthorized',
    'forbidden',
    'internal_error',
    'network_error',
    'invalid_passthrough_provider',
    'passthrough_with_routing',
    'passthrough_missing_provider',
    'passthrough_provider_on_api_key',
    'team_tenant_mismatch',
    'team_not_owned',
    'route_tenant_mismatch',
    'route_not_found',
    'unknown',
] as const
